import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useLang } from "@/LangContext";
import { authAxios, setToken, apiError } from "@/lib/auth";

const inputCls =
  "mt-2 w-full border border-white/15 bg-[#0A0C10] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-colors focus:border-amber-400";

const Field = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="font-code text-[11px] uppercase tracking-[0.3em] text-slate-400">{label}</span>
    <input data-testid={testid} className={inputCls} {...props} />
  </label>
);

export default function PortalAuth({ onAuth }) {
  const { t } = useLang();
  const p = t.portal;
  const [tab, setTab] = useState("login");
  const [f, setF] = useState({ name: "", company: "", email: "", phone: "", password: "", display_mode: "name" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = tab === "login" ? { email: f.email, password: f.password } : { ...f, company: f.company || null };
      const { data } = await authAxios.post(`/auction/${tab}`, body);
      setToken(data.token);
      onAuth(data.bidder);
    } catch (err) {
      toast.error(apiError(err, "Error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="portal-auth" className="border border-white/10 bg-[#0E1119] p-7 sm:p-10">
      <div className="grid grid-cols-2 border border-white/15">
        {["login", "register"].map((k) => (
          <button
            key={k}
            type="button"
            data-testid={`portal-tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-4 py-3 font-display text-xs font-bold uppercase tracking-[0.14em] transition-colors ${
              tab === k ? "bg-amber-400 text-[#0A0C10]" : "text-slate-300 hover:text-white"
            }`}
          >
            {p[k]}
          </button>
        ))}
      </div>
      <form onSubmit={submit} className="mt-8 space-y-5">
        {tab === "register" && (
          <>
            <Field label={p.name} testid="portal-name" value={f.name} onChange={set("name")} required minLength={2} />
            <Field label={p.company} testid="portal-company" value={f.company} onChange={set("company")} />
            <Field label={p.phone} testid="portal-phone" value={f.phone} onChange={set("phone")} required minLength={6} />
          </>
        )}
        <Field label={p.email} testid="portal-email" type="email" value={f.email} onChange={set("email")} required />
        <Field label={p.password} testid="portal-password" type="password" value={f.password} onChange={set("password")} required minLength={8} />
        {tab === "register" && (
          <div>
            <span className="font-code text-[11px] uppercase tracking-[0.3em] text-slate-400">{p.displayMode}</span>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {[["name", p.showName], ["alias", p.showAlias]].map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  data-testid={`portal-display-${v}`}
                  onClick={() => setF({ ...f, display_mode: v })}
                  className={`border px-3 py-3 text-xs font-semibold transition-colors ${
                    f.display_mode === v ? "border-amber-400 bg-amber-400/10 text-amber-400" : "border-white/15 text-slate-300"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        )}
        <button
          type="submit"
          data-testid="portal-submit"
          disabled={loading}
          className="flex w-full items-center justify-center gap-2 bg-amber-400 px-5 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-[#0A0C10] transition-colors hover:bg-amber-300 disabled:opacity-60"
        >
          {loading && <Loader2 size={16} className="animate-spin" />}
          {tab === "login" ? p.submitLogin : p.submitRegister}
        </button>
      </form>
    </div>
  );
}
